import { clsx } from 'clsx';

interface BadgeProps {
  children: React.ReactNode;
  variant?: 'gray' | 'primary' | 'green' | 'yellow' | 'red' | 'blue' | 'purple';
  className?: string;
}

export function Badge({ children, variant = 'gray', className }: BadgeProps) {
  const variants = {
    gray: 'badge-gray',
    primary: 'badge-primary',
    green: 'badge-green',
    yellow: 'badge-yellow',
    red: 'badge-red',
    blue: 'bg-blue-100 dark:bg-blue-950 text-blue-700 dark:text-blue-300',
    purple: 'bg-purple-100 dark:bg-purple-950 text-purple-700 dark:text-purple-300',
  };

  return <span className={clsx('badge', variants[variant], className)}>{children}</span>;
}

// Status badge mappings
export function StatusBadge({ status }: { status: string }) {
  const statusConfig: Record<string, { label: string; variant: BadgeProps['variant'] }> = {
    // Client statuses
    prospect: { label: 'Prospect', variant: 'blue' },
    active: { label: 'Active', variant: 'green' },
    inactive: { label: 'Inactive', variant: 'gray' },
    churned: { label: 'Churned', variant: 'red' },

    // Employee statuses
    on_leave: { label: 'On Leave', variant: 'yellow' },

    // Project statuses
    pipeline: { label: 'Pipeline', variant: 'blue' },
    on_hold: { label: 'On Hold', variant: 'yellow' },
    completed: { label: 'Completed', variant: 'green' },
    cancelled: { label: 'Cancelled', variant: 'gray' },

    // Task statuses
    backlog: { label: 'Backlog', variant: 'gray' },
    todo: { label: 'To Do', variant: 'blue' },
    in_progress: { label: 'In Progress', variant: 'yellow' },
    review: { label: 'In Review', variant: 'purple' },
    done: { label: 'Done', variant: 'green' },
  };

  const config = statusConfig[status] ?? { label: status, variant: 'gray' };

  return <Badge variant={config.variant}>{config.label}</Badge>;
}

export function PriorityBadge({ priority }: { priority: string }) {
  const priorityConfig: Record<string, { label: string; variant: BadgeProps['variant'] }> = {
    critical: { label: 'Critical', variant: 'red' },
    high: { label: 'High', variant: 'yellow' },
    medium: { label: 'Medium', variant: 'blue' },
    low: { label: 'Low', variant: 'gray' },
  };

  const config = priorityConfig[priority] ?? { label: priority, variant: 'gray' };

  return <Badge variant={config.variant}>{config.label}</Badge>;
}
